import createOrie from "@orie/sdk";
import type {
  Event,
  EventMap,
  Product,
  PublishableKey,
  QueryOptions,
  Service,
} from "@orie/sdk";
import type { Enumerable } from "@moirei/dobby";
import type { Entity } from "~/components/elements/event-map/types";

/**
 * Singleton sales API object.
 */
export const salesApi = once((pk?: PublishableKey) => {
  const config = useRuntimeConfig().public.orieSdk;
  const orie = createOrie({
    ...config,
    publishableKey: pk?.token || config.publishableKey,
  });

  return {
    ...orie,
    resetPublishableKey() {
      orie.client.setPublishableKey(pk?.token || config.publishableKey);
    },
  };
});

export const useSalesApi = (pk?: PublishableKey) => {
  const api = salesApi(pk);

  const asyncData = async <T>(key: string, fn: { (): Promise<T> }) => {
    const result = await useAsyncData<T>(key, fn);
    return result.data.value;
  };

  return {
    get product() {
      return {
        async list(options?: QueryOptions) {
          return asyncData<Product[]>("products:list", async () => {
            const result = await api.product.list(options);
            return result?.products || [];
          });
        },

        async retrieve(handle: string, fields?: Enumerable<string>) {
          return asyncData<Product>(`products:${handle}`, async () => {
            const result = await api.product.retrieve(handle, fields);
            return result?.product;
          });
        },
      };
    },
    get service() {
      return {
        async retrieve(handle: string, fields?: Enumerable<string>) {
          return asyncData<Service>(`services:${handle}`, async () => {
            const result = await api.service.retrieve(handle, fields);
            return result?.service;
          });
        },
        async list(options?: QueryOptions) {
          return asyncData<Service[]>("services:list", async () => {
            const result = await api.service.list(options);
            return result?.services || [];
          });
        },
      };
    },
    get event() {
      return {
        async retrieve(handle: string, fields?: Enumerable<string>) {
          return asyncData<Event>(`events:${handle}`, async () => {
            const result = await api.event.retrieve(handle, fields);
            return result?.event;
          });
        },
        async list(options?: QueryOptions) {
          return asyncData<Event[]>("events:list", async () => {
            const result = await api.event.list(options);
            return result?.events || [];
          });
        },

        async eventMap(occurrenceId: number) {
          return asyncData<EventMap<Entity>>(
            `events:map:${occurrenceId}`,
            () => {
              return api.event.map(occurrenceId);
            }
          );
        },
      };
    },
    get profile() {
      return {
        async business(
          provider: string,
          options?: {
            fields?: Enumerable<string>;
            withProducts?: boolean;
            withEvents?: boolean;
            withServices?: boolean;
          }
        ) {
          return asyncData(`business-profile:${provider}`, () => {
            return api.profile.business(provider, options);
          });
        },
        async event(
          provider: string,
          options?: {
            fields?: Enumerable<string>;
            withEvents?: boolean;
          }
        ) {
          return asyncData(`events-profile:${provider}`, () => {
            return api.profile.event(provider, options);
          });
        },

        async service(
          provider: string,
          options?: {
            fields?: Enumerable<string>;
            withProducts?: boolean;
            withEvents?: boolean;
            withServices?: boolean;
          }
        ) {
          return asyncData(`service-profile:${provider}`, () => {
            return api.profile.service(provider, options);
          });
        },
      };
    },
  };
};
